import React, { useMemo } from "react";
import PropTypes from "prop-types";
import TextBlock from "../../../components/Preview/Draggable/Block/TextBlock";
import PopupPitchSkeleton from "../../../components/Preview/catalog-product/PopupPitchSkeleton";
import { parseAiRibbonText } from "../../../../shared/ai-recommendation-copy.js";
import {
  getPitchLayoutDisplay,
  getPitchThemeColors,
  isPitchLayoutSection,
  PITCH_LAYOUT,
  PITCH_TYPOGRAPHY,
} from "../../../../shared/pitch-layout-display.js";
import {
  resolveAiBannerTypography,
} from "../../../../shared/ai-recommendation-upsell-schema.js";
import { isThemeDefaultFontFamily } from "../../../../shared/legacy-font-google.js";
import { buildFontFamilyStyle, resolveInlineFontFamily } from "../../../helper";
import styles from "./ProductRecommendationPitchLine.module.css";

const HEADLINE_SERIF =
  '"Libre Baskerville", Georgia, "Times New Roman", serif';
const LABEL_SANS = '"Aimbrill-Poppins", system-ui, sans-serif';

function toFontStack(fontFamily, fallback = HEADLINE_SERIF) {
  if (isThemeDefaultFontFamily(fontFamily)) {
    return resolveInlineFontFamily(fontFamily);
  }
  const resolved = resolveInlineFontFamily(fontFamily);
  if (resolved) {
    if (/^(sans-serif|serif|monospace|cursive|fantasy|system-ui|inherit)$/i.test(resolved)) {
      return resolved.toLowerCase();
    }
    return resolved.includes(",") ? resolved : `"${resolved}", ${fallback}`;
  }
  return fallback;
}

function resolvePitchFontFamily(section, viewKey) {
  const css = section?.setting?.customCSS?.[viewKey] || {};
  const siblingKey = viewKey === "mobile" ? "desktop" : "mobile";
  const siblingCss = section?.setting?.customCSS?.[siblingKey] || {};
  return css.fontFamily || siblingCss.fontFamily || "";
}

function resolveSurfaceBg(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw.toLowerCase() === "transparent") return "transparent";
  const rgba = raw.match(
    /^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*([\d.]+))?\s*\)$/i
  );
  if (rgba) {
    const alpha = rgba[1] != null ? Number(rgba[1]) : 1;
    if (Number.isFinite(alpha) && alpha <= 0) return "transparent";
  }
  return raw;
}

function stripPitchTextClampCss(css = {}) {
  const {
    WebkitLineClamp: _clamp,
    WebkitBoxOrient: _orient,
    textOverflow: _ellipsis,
    border: _border,
    borderWidth: _borderWidth,
    borderStyle: _borderStyle,
    borderColor: _borderColor,
    borderTop: _borderTop,
    borderRight: _borderRight,
    borderBottom: _borderBottom,
    borderLeft: _borderLeft,
    boxShadow: _boxShadow,
    outline: _outline,
    background: _background,
    backgroundColor: _backgroundColor,
    padding: _padding,
    paddingTop: _paddingTop,
    paddingRight: _paddingRight,
    paddingBottom: _paddingBottom,
    paddingLeft: _paddingLeft,
    height: _height,
    minHeight: _minHeight,
    maxHeight: _maxHeight,
    ...rest
  } = css;
  return {
    ...rest,
    display: "block",
    overflow: "visible",
    whiteSpace: rest.whiteSpace || "pre-wrap",
    height: "auto",
    minHeight: 0,
    maxHeight: "none",
    padding: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    border: "none",
    borderWidth: 0,
    borderStyle: "none",
    borderColor: "transparent",
    background: "transparent",
    backgroundColor: "transparent",
    boxShadow: "none",
    outline: "none",
  };
}

function formatLabelDisplay(label) {
  const raw = String(label || "AI RECOMMENDATION").trim();
  if (/^ai\s+recommendation$/i.test(raw)) {
    return "AI Recommendation";
  }
  return raw;
}

function SparkleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 1.5l.9 2.7 2.7.9-2.7.9L8 8.7l-.9-2.7-2.7-.9 2.7-.9L8 1.5z"
        fill="currentColor"
      />
      <path
        d="M12.5 10l.45 1.35 1.35.45-1.35.45L12.5 13.6l-.45-1.35-1.35-.45 1.35-.45.45-1.35z"
        fill="currentColor"
        opacity="0.85"
      />
    </svg>
  );
}

function HeartIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 13.5s-5-3.2-5-6.8a3 3 0 0 1 5.2-2.05A3 3 0 0 1 13 6.7c0 3.6-5 6.8-5 6.8z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BasketIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M6 8V6.5A4 4 0 0 1 10 2.5 4 4 0 0 1 14 6.5V8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M3.5 8h13l-1.1 9H4.6L3.5 8z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PitchIconImage({ src, alt, className, imgClassName }) {
  if (!src) return null;
  return (
    <span className={className}>
      <img src={src} alt={alt} className={imgClassName} />
    </span>
  );
}

function EditorialIcon({ iconUrl }) {
  if (iconUrl) {
    return (
      <PitchIconImage
        src={iconUrl}
        alt=""
        className={styles.iconSquare}
        imgClassName={styles.iconSquareImg}
      />
    );
  }
  return (
    <span className={styles.iconSquare} aria-hidden>
      <SparkleIcon className={styles.iconSquareSvg} />
    </span>
  );
}

function AccentIcon({ iconUrl }) {
  if (iconUrl) {
    return (
      <PitchIconImage
        src={iconUrl}
        alt=""
        className={styles.accentBox}
        imgClassName={styles.accentBoxImg}
      />
    );
  }
  return (
    <span className={styles.accentBox} aria-hidden>
      <BasketIcon className={styles.accentBoxSvg} />
    </span>
  );
}

function PitchBottomDivider({ show }) {
  if (!show) return null;
  return <span className={styles.hDivider} aria-hidden />;
}

export default function ProductRecommendationPitchLine({
  section,
  popupData,
  activeStep,
  index,
  displayText,
  loading = false,
  isMobile = false,
}) {
  const display = getPitchLayoutDisplay(section);
  const colors = getPitchThemeColors(section);
  const layout = display.layout;
  const { label, summary } = parseAiRibbonText(displayText ?? section?.text ?? "");
  const viewKey = isMobile ? "mobile" : "desktop";
  const isAiBanner = section?.setting?.role === "aiBanner";
  const isPitchLayoutAiBanner = isAiBanner && section?.setting?.pitchLayout === true;
  const usesPitchTextFlow = isPitchLayoutSection(section);
  const iconUrl = String(display.iconUrl || "").trim();
  const showDivider = false;
  const badgeText =
    display.showBadge === false
      ? ""
      : String(display.badgeText || "Handpicked for you").trim();
  const labelDisplay = formatLabelDisplay(label);
  const surfaceBg = resolveSurfaceBg(colors.backgroundColor);

  const pitchTextAlign =
    layout === PITCH_LAYOUT.CENTERED_CLASSIC ? "center" : "left";

  const typo = useMemo(() => {
    const css = section?.setting?.customCSS?.[viewKey] || {};
    const siblingCss = section?.setting?.customCSS?.[viewKey === "mobile" ? "desktop" : "mobile"] || {};
    const mergedCss = { ...siblingCss, ...css };
    const pitchBodySize = PITCH_TYPOGRAPHY.bodyFontSize;
    if (isPitchLayoutAiBanner) {
      const stripLayout = layout === PITCH_LAYOUT.SOFT_STRIP;
      return {
        fontFamily: isThemeDefaultFontFamily(mergedCss.fontFamily)
          ? mergedCss.fontFamily
          : mergedCss.fontFamily || (stripLayout ? LABEL_SANS : HEADLINE_SERIF),
        fontSize: mergedCss.fontSize || pitchBodySize,
        fontWeight: mergedCss.fontWeight || (stripLayout ? "500" : "600"),
        lineHeight: mergedCss.lineHeight || (stripLayout ? "1.4" : "1.35"),
        color: mergedCss.color || colors.textColor || "#0f172a",
        textAlign: mergedCss.textAlign || pitchTextAlign,
      };
    }
    if (isAiBanner) {
      const resolved = resolveAiBannerTypography(section, viewKey);
      return {
        ...resolved,
        fontSize: css.fontSize || pitchBodySize,
      };
    }
    return {
      fontFamily: isThemeDefaultFontFamily(css.fontFamily)
        ? css.fontFamily
        : css.fontFamily || HEADLINE_SERIF,
      fontSize: css.fontSize || pitchBodySize,
      fontWeight: css.fontWeight || "600",
      lineHeight: css.lineHeight || "1.35",
      color: css.color || colors.textColor,
      textAlign: css.textAlign || "center",
    };
  }, [isPitchLayoutAiBanner, isAiBanner, section, viewKey, colors.textColor, layout, pitchTextAlign]);

  const pitchTextColor = typo.color;

  const pitchFontStack = useMemo(
    () => toFontStack(resolvePitchFontFamily(section, viewKey) || typo.fontFamily, HEADLINE_SERIF),
    [section, viewKey, typo.fontFamily]
  );

  const pitchLabelFontStack = useMemo(
    () =>
      toFontStack(
        resolvePitchFontFamily(section, viewKey) || typo.fontFamily,
        LABEL_SANS
      ),
    [section, viewKey, typo.fontFamily]
  );

  const styleVars = useMemo(() => {
    const css = section?.setting?.customCSS?.[viewKey] || {};
    return {
      ...(pitchFontStack ? { "--pr-pitch-font": pitchFontStack } : {}),
      "--pr-pitch-label-font": pitchLabelFontStack || "inherit",
      "--pr-pitch-size": typo.fontSize,
      "--pr-pitch-weight": typo.fontWeight,
      "--pr-pitch-line-height": typo.lineHeight,
      "--pr-pitch-color": pitchTextColor,
      "--pr-pitch-label-color": colors.labelColor,
      "--pr-pitch-pill-color": colors.labelColor,
      "--pr-pitch-bg": surfaceBg,
      "--pr-pitch-border": colors.borderColor || css.borderColor,
      "--pr-pitch-divider": colors.dividerColor || colors.borderColor || css.borderColor,
      "--pr-pitch-badge-bg": resolveSurfaceBg(colors.badgeBackgroundColor),
      "--pr-pitch-pill-bg": colors.pillBackgroundColor || "#f5f0eb",
      "--pr-pitch-badge-color": colors.badgeTextColor,
      "--pr-pitch-icon-bg": colors.iconBackgroundColor,
      "--pr-pitch-icon-color": colors.iconColor,
      "--pr-pitch-accent-bg": colors.accentBackgroundColor,
      "--pr-pitch-accent-color": colors.accentIconColor,
      "--pr-pitch-label-size":
        display.labelFontSize || PITCH_TYPOGRAPHY.labelFontSize,
      "--pr-pitch-subtitle-size": PITCH_TYPOGRAPHY.subtitleFontSize,
      "--pr-pitch-badge-size":
        display.badgeFontSize || PITCH_TYPOGRAPHY.subtitleFontSize,
    };
  }, [typo, colors, pitchTextColor, surfaceBg, section, viewKey, pitchFontStack, pitchLabelFontStack, display.labelFontSize, display.badgeFontSize]);

  const pitchSummarySection = useMemo(() => {
    if (!summary) return null;
    const rawDesktopCss = section?.setting?.customCSS?.desktop || {};
    const rawMobileCss = section?.setting?.customCSS?.mobile || {};
    const desktopCss = usesPitchTextFlow
      ? stripPitchTextClampCss(rawDesktopCss)
      : rawDesktopCss;
    const mobileCss = usesPitchTextFlow
      ? stripPitchTextClampCss(rawMobileCss)
      : rawMobileCss;
    const desktopAlign = pitchTextAlign === "center" ? "center" : "left";
    const mobileAlign =
      layout === PITCH_LAYOUT.SOFT_STRIP
        ? "left"
        : layout === PITCH_LAYOUT.PREMIUM_RIBBON
          ? "left"
          : desktopAlign;
    const toJustify = (align) => (align === "center" ? "center" : "flex-start");

    return {
      ...section,
      text: summary,
      setting: {
        ...section.setting,
        customCSS: {
          ...section.setting?.customCSS,
          desktop: {
            ...desktopCss,
            textAlign: desktopAlign,
            justifyContent: toJustify(desktopAlign),
          },
          mobile: {
            ...mobileCss,
            textAlign: mobileAlign,
            justifyContent: toJustify(mobileAlign),
          },
        },
      },
    };
  }, [section, summary, pitchTextAlign, layout, usesPitchTextFlow]);

  const summaryNode = loading ? (
    <div className={styles.summary}>
      <PopupPitchSkeleton compact={isMobile} />
    </div>
  ) : pitchSummarySection ? (
    <div
      className={`${styles.summary}${usesPitchTextFlow ? ` ${styles.summaryFull}` : ""}`}
      style={pitchFontStack ? { fontFamily: pitchFontStack } : undefined}
    >
      <TextBlock
        popupData={popupData}
        activeStep={activeStep}
        index={index}
        section={pitchSummarySection}
        catalogFlow
      />
    </div>
  ) : null;

  const subtitleNode =
    badgeText && layout === PITCH_LAYOUT.CENTERED_CLASSIC ? (
      <p className={styles.subtitle}>{badgeText}</p>
    ) : null;

  const badgeNode =
    badgeText && layout === PITCH_LAYOUT.PREMIUM_RIBBON ? (
      <span className={styles.badge}>
        <HeartIcon className={styles.badgeIcon} />
        {badgeText}
      </span>
    ) : null;

  const rootClass = [
    styles.root,
    layout === PITCH_LAYOUT.PREMIUM_RIBBON && styles.layoutRibbon,
    layout === PITCH_LAYOUT.SOFT_STRIP && styles.layoutStrip,
    layout === PITCH_LAYOUT.CENTERED_CLASSIC && styles.layoutClassic,
    surfaceBg !== "transparent" && styles.hasSurfaceBg,
    isPitchLayoutAiBanner && styles.aiBannerPitch,
    isMobile && styles.mobileLayout,
  ]
    .filter(Boolean)
    .join(" ");

  if (layout === PITCH_LAYOUT.PREMIUM_RIBBON) {
    return (
      <div
        className={rootClass}
        style={styleVars}
        data-pitch-layout={layout}
        data-loading={loading ? "true" : undefined}
      >
        <div className={styles.ribbonRow}>
          <EditorialIcon iconUrl={iconUrl} />
          <span className={styles.vDivider} aria-hidden />
          <div className={styles.meta}>
            <span className={styles.label}>{labelDisplay.toUpperCase()}</span>
            {summaryNode}
            {badgeNode}
          </div>
        </div>
        <PitchBottomDivider show={showDivider} />
      </div>
    );
  }

  if (layout === PITCH_LAYOUT.SOFT_STRIP) {
    return (
      <div
        className={rootClass}
        style={styleVars}
        data-pitch-layout={layout}
        data-loading={loading ? "true" : undefined}
      >
        <div className={styles.stripInner}>
          <div className={styles.stripContent}>
            <span className={styles.stripLabel}>
              <SparkleIcon className={styles.stripLabelIcon} />
              {labelDisplay.toUpperCase()}
            </span>
            {summaryNode}
          </div>
          <AccentIcon iconUrl={iconUrl} />
        </div>
        <PitchBottomDivider show={showDivider} />
      </div>
    );
  }

  return (
    <div
      className={rootClass}
      style={styleVars}
      data-pitch-layout={layout}
      data-loading={loading ? "true" : undefined}
    >
      <div className={styles.classicInner}>
        <span className={styles.labelPill}>
          <SparkleIcon className={styles.labelPillIcon} />
          {labelDisplay}
        </span>
        {summaryNode}
        {subtitleNode}
      </div>
      <PitchBottomDivider show={showDivider} />
    </div>
  );
}

ProductRecommendationPitchLine.propTypes = {
  section: PropTypes.object,
  popupData: PropTypes.object,
  activeStep: PropTypes.number,
  index: PropTypes.number,
  displayText: PropTypes.string,
  loading: PropTypes.bool,
  isMobile: PropTypes.bool,
};
